// Accessible popover menu (WAI-ARIA menu button pattern), no dependency.
// The default trigger is a `moreH` icon button; a caller can pass a
// `trigger` render prop to use any element (the sidebar profile row) and
// must spread `props` and attach `ref` on it. Styles: `.menu` in styles.css.
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode, type RefObject } from "react";
import { Glyph, type GlyphName } from "./icons";

export interface MenuItem {
  id: string;
  label: string;
  icon?: GlyphName;
  tone?: "danger";
  disabled?: boolean;
  /**
   * Set on every item of a single-choice menu (the Mode menu): the items
   * become menuitemradio with aria-checked, and a check lane is reserved
   * before every label so the checked one is marked without moving text.
   */
  checked?: boolean;
  onSelect: () => void;
}

export interface MenuTriggerProps {
  id: string;
  "aria-haspopup": "menu";
  "aria-expanded": boolean;
  "aria-controls": string | undefined;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
}

export interface MenuProps {
  /** Accessible name of the trigger (default trigger) and of the menu. */
  label: string;
  items: MenuItem[];
  align?: "start" | "end";
  placement?: "below" | "above";
  trigger?: (p: { ref: RefObject<HTMLButtonElement | null>; props: MenuTriggerProps }) => ReactNode;
  className?: string;
}

/** The viewport's top and bottom, narrowed by every ancestor that clips its overflow. */
export function visibleArea(el: Element): { top: number; bottom: number } {
  let top = 0;
  let bottom = window.innerHeight || document.documentElement.clientHeight;
  for (let box = el.parentElement; box && box !== document.body && box !== document.documentElement; box = box.parentElement) {
    if (!/(auto|scroll|hidden|clip)/.test(getComputedStyle(box).overflowY)) continue;
    const r = box.getBoundingClientRect();
    top = Math.max(top, r.top);
    bottom = Math.min(bottom, r.bottom);
  }
  return { top, bottom };
}

export function Menu({ label, items, align = "end", placement = "below", trigger, className }: MenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [flipped, setFlipped] = useState<"below" | "above" | null>(null);
  const pendingFocus = useRef<"first" | "last">("first");
  const uid = useId();
  const listId = `${uid}-menu`;
  const triggerId = `${uid}-trigger`;

  const enabled = useCallback((): HTMLButtonElement[] => Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled), [role="menuitemradio"]:not(:disabled)') ?? []), []);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  // Placement: the asked side, flipped when the list would leave the
  // visible area there and the other side has more room. The visible area
  // is the viewport cut by every clipping ancestor (the sidebar's scroll
  // box: a Riwayat row near its end would hide Hapus under the foot).
  useLayoutEffect(() => {
    if (!open) {
      setFlipped(null);
      return;
    }
    const list = listRef.current;
    const root = rootRef.current;
    if (!list || !root) return;
    const box = list.getBoundingClientRect();
    const anchor = root.getBoundingClientRect();
    const { top, bottom } = visibleArea(root);
    const roomBelow = bottom - anchor.bottom;
    const roomAbove = anchor.top - top;
    if (placement === "below" && box.bottom > bottom && roomAbove > roomBelow) setFlipped("above");
    else if (placement === "above" && box.top < top && roomBelow > roomAbove) setFlipped("below");
  }, [open, placement]);

  // Move focus into the menu on open (roving tabindex: items are tabIndex -1,
  // focus is moved by the arrow keys, Tab closes the menu).
  useEffect(() => {
    if (!open) return;
    const list = enabled();
    (pendingFocus.current === "last" ? list[list.length - 1] : list[0])?.focus();
  }, [open, enabled]);

  // Outside pointerdown closes without stealing focus from what was clicked.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const openAt = (which: "first" | "last") => {
    pendingFocus.current = which;
    setOpen(true);
  };

  const triggerProps: MenuTriggerProps = {
    id: triggerId,
    "aria-haspopup": "menu",
    "aria-expanded": open,
    "aria-controls": open ? listId : undefined,
    onClick: () => {
      if (open) close(false);
      else openAt("first");
    },
    onKeyDown: (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        openAt("first");
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        openAt("last");
      }
    },
  };

  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = enabled();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const go = (i: number) => {
      e.preventDefault();
      list[(i + list.length) % list.length]?.focus();
    };
    switch (e.key) {
      case "ArrowDown":
        return go(at + 1);
      case "ArrowUp":
        return go(at < 0 ? list.length - 1 : at - 1);
      case "Home":
        return go(0);
      case "End":
        return go(list.length - 1);
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        return close(true);
      case "Tab":
        return close(false);
    }
  };

  const select = (item: MenuItem) => {
    if (item.disabled) return;
    setOpen(false);
    item.onSelect();
    // Return focus to the trigger unless the action moved it somewhere real
    // (a dialog, an input). Focus left on the removed item falls to body.
    setTimeout(() => {
      const a = document.activeElement;
      if (!a || a === document.body || listRef.current?.contains(a)) triggerRef.current?.focus();
    }, 0);
  };

  return (
    <div ref={rootRef} className={className ? `menu ${className}` : "menu"} data-open={open ? "true" : undefined}>
      {trigger ? (
        trigger({ ref: triggerRef, props: triggerProps })
      ) : (
        <button ref={triggerRef} type="button" className="ui-icon-btn menu-trigger" aria-label={label} {...triggerProps}>
          <Glyph name="moreH" />
        </button>
      )}
      {open ? (
        <div ref={listRef} id={listId} role="menu" aria-label={label} className="menu-list" data-align={align} data-placement={flipped ?? placement} onKeyDown={onListKey}>
          {items.map((item) => {
            const radio = item.checked !== undefined;
            return (
              <button
                key={item.id}
                type="button"
                role={radio ? "menuitemradio" : "menuitem"}
                aria-checked={radio ? item.checked === true : undefined}
                tabIndex={-1}
                className="menu-item"
                data-tone={item.tone}
                disabled={item.disabled}
                onClick={() => select(item)}
              >
                {radio ? (
                  <span className="menu-check" aria-hidden="true">
                    {item.checked ? <Glyph name="check" size={18} /> : null}
                  </span>
                ) : null}
                {item.icon ? <Glyph name={item.icon} size={20} /> : null}
                <span className="menu-label">{item.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
